/**
 * @fileoverview Component for the success page, which is the final step of the Listings Launch flow.
 *
 * @author Nikhil Ghate (nghate@yext.com)
 */

import {ListingsLaunchContext} from '/src/com/yext/listingslaunchstorm/js/contexts/listingslaunchcontextprovider';
import {EntityInfo} from '/src/com/yext/listingslaunchstorm/js/data/entityinfo';
import {VerificationMethod} from '/src/com/yext/listingslaunchstorm/js/data/verificationmethod';
import {Button} from '/ui/components/button/button';
import {LISTINGS_ASSET} from '/ui/components/marketingactivationpage/images/listings';
import {MarketingActivationPage} from '/ui/components/marketingactivationpage/marketingactivationpage';
import {Tooltip} from '/ui/components/tooltip/tooltip';
import {useIsSmallViewport} from '/ui/lib/responsivity';

import * as styles from '/src/com/yext/listingslaunchstorm/js/components/styles/success.module.scss';
import ErrorSvg from '/src/com/yext/listingslaunchstorm/public/icons/error.svg';
import FilledCheckSvg from '/src/com/yext/listingslaunchstorm/public/icons/filled-checkmark.svg';
import CheckedSvg from '/src/com/yext/listingslaunchstorm/public/icons/green-checkmark.svg';
import HourglassSvg from '/src/com/yext/listingslaunchstorm/public/icons/hourglass.svg';

/**
 * @returns {React.ReactNode}
 */
function Description() {
  return (
    <div className={styles.description}>
      {yext.msg('This is the end of the setup. If you have not yet verified your location on Google, you can come back to the step and verify it later. You can now close this window.')}
    </div>
  );
}

function ListItem({entity, icon}) {
  return (
    <li key={entity.id} className={styles.listItemContainer}>
      <div className={styles.listItemContent}>
        {icon}
        <div className={styles.entityName}>{entity.name}</div>
      </div>
    </li>
  );
}

function ErrorIcon() {
  return (
    <Tooltip tooltipText={yext.msg('Entity Not Verified')}>
      <ErrorSvg />
    </Tooltip>
  );
}

function PendingIcon() {
  return (
    <Tooltip tooltipText={yext.msg('Entity Pending Verification')}>
      <HourglassSvg />
    </Tooltip>
  );
}

/**
 * @returns {React.ReactNode}
 */
function Summary() {
  const {linkedEntities, verifiedListings} = React.useContext(ListingsLaunchContext);

  const [showAllItemsMatch, setShowAllItemsMatch] = React.useState(false);
  const [showAllItemsVerification, setShowAllItemsVerification] = React.useState(false);

  const maxItemsToShow = 2;

  const handleToggleMatchList = () => {
    setShowAllItemsMatch(!showAllItemsMatch);
  };

  const handleToggleVerificationList = () => {
    setShowAllItemsVerification(!showAllItemsVerification);
  };

  return (
    <div className={styles.summaryContainer}>
      <div className={styles.listContainer}>
        <div className={styles.listHeading}>
          <FilledCheckSvg />
          <h5 className={styles.listTitle}>{yext.msg('1. Google Business Profile')}</h5>
        </div>
        <div className={styles.bar}></div>
        <ul className={styles.listItemContainer}>
          <div className={styles.listItemContent}>
            <CheckedSvg />
            <div className={styles.entityName}>{yext.msg('Linked Accounts')}</div>
          </div>
        </ul>
      </div>

      <div className={styles.listContainer}>
        <div className={styles.listHeading}>
          <FilledCheckSvg />
          <h5 className={styles.listTitle}>{yext.msg('2. Match Entities')}</h5>
        </div>
        <div className={styles.bar}></div>
        <ul>
          {showAllItemsMatch
            ? linkedEntities.map(entity => (
              <ListItem key={entity.id} entity={entity} icon={<CheckedSvg/>} />
            ))
            : linkedEntities.slice(0, maxItemsToShow).map(entity => (
              <ListItem key={entity.id} entity={entity} icon={<CheckedSvg/>} />
            ))}
        </ul>
        {!showAllItemsMatch && linkedEntities.length > maxItemsToShow && (
          <button className={styles.showMoreButton} onClick={handleToggleMatchList} type="button">{yext.msg('Show More')}</button>
        )}
        {showAllItemsMatch && (
          <button className={styles.showMoreButton} onClick={handleToggleMatchList} type="button">{yext.msg('Show Less')}</button>
        )}
      </div>

      <div className={styles.listContainer}>
        <div className={styles.listHeading}>
          {linkedEntities.every(entity => getIsVerified(entity, verifiedListings) === verificationStatus.Verified) ? <FilledCheckSvg /> : <ErrorIcon/>}
          <h5 className={styles.listTitle}>{yext.msg('3. Verification')}</h5>
        </div>
        <div className={styles.bar}></div>
        <ul>
          {showAllItemsVerification
            ? linkedEntities.map(entity => (
              <ListItem
                key={entity.id}
                entity={entity}
                icon={(() => {
                  switch (getIsVerified(entity, verifiedListings)) {
                    case verificationStatus.Verified:
                      return <CheckedSvg />;
                    case verificationStatus.Pending:
                      return <PendingIcon />;
                    default:
                      return <ErrorIcon />;
                  }
                })()} />
            ))
            : linkedEntities.slice(0, maxItemsToShow).map(entity => (
              <ListItem
                key={entity.id}
                entity={entity}
                icon={(() => {
                  switch (getIsVerified(entity, verifiedListings)) {
                    case verificationStatus.Verified:
                      return <CheckedSvg />;
                    case verificationStatus.Pending:
                      return <PendingIcon />;
                    default:
                      return <ErrorIcon />;
                  }
                })()} />
            ))}
        </ul>
        {!showAllItemsVerification && linkedEntities.length > maxItemsToShow && (
          <button className={styles.showMoreButton} onClick={handleToggleVerificationList} type="button">{yext.msg('Show More')}</button>
        )}
        {showAllItemsVerification && (
          <button className={styles.showMoreButton} onClick={handleToggleVerificationList} type="button">{yext.msg('Show Less')}</button>
        )}
      </div>
    </div>
  );
}

const verificationStatus = {
  Verified: 'Verified',
  Unverified: 'Unverified',
  Pending: 'Pending',
};

function getIsVerified(entity, verifiedListings) {
  const method = verifiedListings.find(v => v.entityId === entity.id)?.method;
  switch (method) {
    case VerificationMethod.VIDEO:
    case VerificationMethod.SKIP:
      return verificationStatus.Unverified;
    case VerificationMethod.DISABLED:
    case VerificationMethod.POSTCARD:
      return verificationStatus.Pending;
    default:
      return verificationStatus.Verified;
  }
}

/**
 * @returns {React.ReactNode}
 */
export function Success() {
  const isSmallViewport = useIsSmallViewport();

  const params = new URLSearchParams(window.location.search);
  const continueUrl = params.get('continueUrl');

  return (
    <div className={styles.container}>
      <div className={styles.success}>
        <MarketingActivationPage
          image={LISTINGS_ASSET}
          header={yext.msg('Summary')}
          description={<Description/>}>
        </MarketingActivationPage>
        <Summary />
        {continueUrl && <div className={styles.continueUrlButton}>
          <Button
            context="primary"
            fullWidth={isSmallViewport}
            onClick={() => {
              window.location.href = continueUrl;
            }}
          >
            {yext.msg('Continue')}
          </Button>
        </div>}
      </div>
    </div>
  );
}
